import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import type {
  Category,
  CategoryNode,
  CategoryType,
  CreateCategoryBody,
  UpdateCategoryBody,
} from '@lira/shared';
import { apiPatch, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import {
  CategoryIcon,
  ColorSwatches,
  CURATED_ICON_NAMES,
  IconPicker,
  Sheet,
} from '@/components/ui';

interface CategoryFormModalProps {
  open: boolean;
  onClose: () => void;
  type: CategoryType;
  /** Presente ao criar subcategoria (herda tipo e cor deste pai). */
  parent?: CategoryNode;
  /** Presente ao editar uma categoria existente. */
  category?: Category;
}

const TYPE_COLOR: Record<CategoryType, string> = { income: '#4CAF50', expense: '#F44336' };
const DEFAULT_ICON = CURATED_ICON_NAMES[0] ?? 'shopping-cart';

/** Cria categoria-pai ou subcategoria; edita categoria existente. Filho herda a cor do pai. */
export function CategoryFormModal({ open, onClose, type, parent, category }: CategoryFormModalProps) {
  const queryClient = useQueryClient();

  const isChild = useMemo(
    () => Boolean(parent) || (category != null && category.parentId != null),
    [parent, category],
  );

  const [name, setName] = useState('');
  const [icon, setIcon] = useState<string>(DEFAULT_ICON);
  const [color, setColor] = useState<string>(TYPE_COLOR[type]);

  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? '');
    setIcon(category?.icon ?? DEFAULT_ICON);
    setColor(category?.color ?? parent?.color ?? TYPE_COLOR[type]);
  }, [open, category, parent, type]);

  const mutation = useMutation({
    mutationFn: () => {
      if (category) {
        const body: UpdateCategoryBody = isChild ? { name: name.trim(), icon } : { name: name.trim(), icon, color };
        return apiPatch<{ category: Category }>(`/categories/${category.id}`, body);
      }
      const body: CreateCategoryBody = {
        name: name.trim(),
        type: parent?.type ?? type,
        icon,
        ...(isChild ? { parentId: parent?.id } : { color }),
      };
      return apiPost<{ category: Category }>('/categories', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['categories'] });
      void queryClient.invalidateQueries({ queryKey: ['expense-calendar'] });
      onClose();
    },
  });

  const canSave = name.trim().length > 0 && !mutation.isPending;
  const effectiveColor = isChild ? (parent?.color ?? category?.color ?? TYPE_COLOR[type]) : color;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSave) return;
    mutation.mutate();
  };

  const title = category
    ? 'Editar categoria'
    : isChild
      ? 'Nova subcategoria'
      : 'Nova categoria';

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="flex items-center gap-3">
          <CategoryIcon icon={icon} color={effectiveColor} size={48} fallbackText={name} />
          <div className="flex-1">
            <label htmlFor="category-name" className="mb-1 block text-[13px] text-muted">
              Nome
            </label>
            <input
              id="category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              placeholder={isChild ? 'Ex.: Supermercado' : 'Ex.: Alimentação'}
              autoComplete="off"
              className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        {parent && (
          <p className="rounded-lg bg-canvas px-3 py-2 text-[13px] text-muted">
            Subcategoria de <span className="font-medium text-ink">{parent.name}</span>
          </p>
        )}

        <div>
          <label className="mb-2 block text-[13px] text-muted">Ícone</label>
          <IconPicker value={icon} onChange={setIcon} accentColor={effectiveColor} />
        </div>

        {isChild ? (
          <p className="text-[13px] text-muted">A cor é herdada da categoria pai.</p>
        ) : (
          <div>
            <label className="mb-2 block text-[13px] text-muted">Cor</label>
            <ColorSwatches value={color} onChange={setColor} />
          </div>
        )}

        {mutation.isError && <p className="text-sm text-expense">{errorMessage(mutation.error)}</p>}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-canvas hover:text-ink"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </form>
    </Sheet>
  );
}
